import { useEffect, useRef, useState } from 'react';
import {
  AUTO_MODELLEN,
  AUTO_FALLBACK,
  MERKEN,
  MERK_FALLBACK_LABEL,
  ORIENTATIE_CATEGORIEEN,
  PHEV_ORIENTATIE_TYPEN,
  EV_ORIENTATIE_TYPEN,
} from './autoDatabase';
import type { AutoModel, OrientatieCategorieId } from './autoDatabase';

export interface StapJouwAutoState {
  heeftEV: boolean | null;
  autoModel: AutoModel | null;
  categorie: OrientatieCategorieId | null;
  /** Only relevant when heeftEV === false */
  orientatieType: 'ev' | 'phev' | null;
}

interface Props {
  state: StapJouwAutoState;
  onChange: (next: StapJouwAutoState) => void;
  onNext: () => void;
}

export default function StapJouwAuto({ state, onChange, onNext }: Props) {
  const [merkZoek, setMerkZoek] = useState('');
  const zoekInputRef = useRef<HTMLInputElement>(null);

  const selectedMerk =
    state.autoModel === AUTO_FALLBACK
      ? MERK_FALLBACK_LABEL
      : (state.autoModel?.merk ?? '');

  const modelsForMerk = AUTO_MODELLEN.filter((a) => a.merk === selectedMerk);

  useEffect(() => {
    if (state.heeftEV === true) {
      const timer = setTimeout(() => zoekInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [state.heeftEV]);

  useEffect(() => {
    if (selectedMerk) setMerkZoek('');
  }, [selectedMerk]);

  function handleHeeftEV(value: boolean) {
    onChange({ heeftEV: value, autoModel: null, categorie: null, orientatieType: null });
    setMerkZoek('');
  }

  function handleOrientatieType(type: 'ev' | 'phev') {
    onChange({ ...state, orientatieType: type, categorie: null, autoModel: null });
  }

  function handleMerkSelect(merk: string) {
    if (merk === MERK_FALLBACK_LABEL) {
      onChange({ ...state, autoModel: AUTO_FALLBACK });
      return;
    }
    const hasModels = AUTO_MODELLEN.some((a) => a.merk === merk);
    if (!hasModels) {
      onChange({ ...state, autoModel: { ...AUTO_FALLBACK, merk } });
      return;
    }
    const first = AUTO_MODELLEN.find((a) => a.merk === merk) ?? null;
    onChange({ ...state, autoModel: first });
  }

  function handleModelChange(modelNaam: string) {
    if (modelNaam === AUTO_FALLBACK.model) {
      onChange({ ...state, autoModel: AUTO_FALLBACK });
      return;
    }
    const found =
      AUTO_MODELLEN.find((a) => a.merk === selectedMerk && a.model === modelNaam) ?? null;
    onChange({ ...state, autoModel: found });
  }

  function handleCategorieChange(cat: string) {
    const categorie = cat as OrientatieCategorieId;
    const evType = EV_ORIENTATIE_TYPEN.find((t) => t.categorie === categorie);
    onChange({ ...state, categorie, autoModel: evType?.model ?? AUTO_FALLBACK });
  }

  function handlePhevTypeSelect(phevId: string) {
    const phevType = PHEV_ORIENTATIE_TYPEN.find((t) => t.id === phevId);
    if (phevType) onChange({ ...state, autoModel: phevType.model });
  }

  const query = merkZoek.trim().toLowerCase();
  const gefilterdeMerken = query
    ? MERKEN.filter((m) => m.toLowerCase().includes(query))
    : MERKEN;

  function handleZoekKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      if (gefilterdeMerken.length === 1) handleMerkSelect(gefilterdeMerken[0]);
      else if (gefilterdeMerken.length === 0) handleMerkSelect(MERK_FALLBACK_LABEL);
    }
  }

  const canProceed =
    state.heeftEV === true
      ? state.autoModel !== null
      : state.heeftEV === false && state.orientatieType !== null &&
        state.autoModel !== null;

  // EV orientatie categories (excludes PHEV — handled by orientatieType toggle)
  const evCategorieen = ORIENTATIE_CATEGORIEEN.filter(
    (c) => c !== ('Plug-in hybride (PHEV)' as string),
  );

  return (
    <div className="lc-stap lc-stap--auto">
      <h2 className="lc-stap__titel">Stap 1 — Jouw auto</h2>

      {/* EV yes/no */}
      <fieldset className="lc-fieldset">
        <legend className="lc-label">Heb je al een elektrische auto?</legend>
        <div className="lc-toggle-groep">
          <button
            type="button"
            className={`lc-toggle${state.heeftEV === true ? ' lc-toggle--actief' : ''}`}
            onClick={() => handleHeeftEV(true)}
          >
            Ja
          </button>
          <button
            type="button"
            className={`lc-toggle${state.heeftEV === false ? ' lc-toggle--actief' : ''}`}
            onClick={() => handleHeeftEV(false)}
          >
            Nee, ik oriënteer me
          </button>
        </div>
      </fieldset>

      {/* EV owner — merk + model */}
      {state.heeftEV === true && (
        <div className="lc-auto-keuze">
          <div className="lc-veld">
            <label className="lc-label" htmlFor="lc-merk-zoek">
              Merk
            </label>
            <input
              ref={zoekInputRef}
              id="lc-merk-zoek"
              type="text"
              className="lc-input lc-merk-zoek"
              placeholder="Zoek merk…"
              value={merkZoek}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(e) => setMerkZoek(e.target.value)}
              onKeyDown={handleZoekKeyDown}
              aria-label="Zoek automerk"
            />

            {(!selectedMerk || merkZoek) && (
              <ul className="lc-merk-lijst" role="listbox" aria-label="Automerken">
                {gefilterdeMerken.map((merk) => (
                  <li key={merk} role="option" aria-selected={selectedMerk === merk}>
                    <button
                      type="button"
                      className={`lc-merk-item${selectedMerk === merk ? ' lc-merk-item--actief' : ''}`}
                      onClick={() => handleMerkSelect(merk)}
                    >
                      {merk}
                    </button>
                  </li>
                ))}
                {(gefilterdeMerken.length === 0 || !query) && (
                  <li role="option" aria-selected={selectedMerk === MERK_FALLBACK_LABEL}>
                    <button
                      type="button"
                      className={`lc-merk-item lc-merk-item--fallback${selectedMerk === MERK_FALLBACK_LABEL ? ' lc-merk-item--actief' : ''}`}
                      onClick={() => handleMerkSelect(MERK_FALLBACK_LABEL)}
                    >
                      {MERK_FALLBACK_LABEL}
                    </button>
                  </li>
                )}
                {query && gefilterdeMerken.length > 0 && (
                  <li role="option" aria-selected={selectedMerk === MERK_FALLBACK_LABEL}>
                    <button
                      type="button"
                      className={`lc-merk-item lc-merk-item--fallback${selectedMerk === MERK_FALLBACK_LABEL ? ' lc-merk-item--actief' : ''}`}
                      onClick={() => handleMerkSelect(MERK_FALLBACK_LABEL)}
                    >
                      {MERK_FALLBACK_LABEL}
                    </button>
                  </li>
                )}
              </ul>
            )}

            {selectedMerk && !merkZoek && (
              <div className="lc-merk-geselecteerd">
                <span className="lc-merk-geselecteerd__naam">{selectedMerk}</span>
                <button
                  type="button"
                  className="lc-merk-geselecteerd__wijzig"
                  onClick={() => {
                    onChange({ ...state, autoModel: null });
                    setTimeout(() => zoekInputRef.current?.focus(), 50);
                  }}
                  aria-label="Ander merk kiezen"
                >
                  Wijzig
                </button>
              </div>
            )}
          </div>

          {selectedMerk && selectedMerk !== MERK_FALLBACK_LABEL && modelsForMerk.length > 0 && (
            <div className="lc-veld">
              <label className="lc-label" htmlFor="lc-model">
                Model
              </label>
              <select
                id="lc-model"
                className="lc-select"
                value={state.autoModel?.model ?? ''}
                onChange={(e) => handleModelChange(e.target.value)}
              >
                <option value="">— Kies model —</option>
                {modelsForMerk.map((a) => (
                  <option key={a.model} value={a.model}>
                    {a.model}
                  </option>
                ))}
                <option value={AUTO_FALLBACK.model}>{AUTO_FALLBACK.model}</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Orienter — step 1: EV or PHEV toggle */}
      {state.heeftEV === false && (
        <>
          <fieldset className="lc-fieldset">
            <legend className="lc-label">Welk type auto overweeg je?</legend>
            <div className="lc-toggle-groep">
              <button
                type="button"
                className={`lc-toggle${state.orientatieType === 'ev' ? ' lc-toggle--actief' : ''}`}
                onClick={() => handleOrientatieType('ev')}
              >
                Volledig elektrisch
              </button>
              <button
                type="button"
                className={`lc-toggle${state.orientatieType === 'phev' ? ' lc-toggle--actief' : ''}`}
                onClick={() => handleOrientatieType('phev')}
              >
                Plug-in hybride (PHEV)
              </button>
            </div>
          </fieldset>

          {/* EV sub-category */}
          {state.orientatieType === 'ev' && (
            <fieldset className="lc-fieldset">
              <legend className="lc-label">Welk formaat elektrische auto overweeg je?</legend>
              <div className="lc-toggle-groep lc-toggle-groep--vertical">
                {evCategorieen.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`lc-toggle${state.categorie === cat ? ' lc-toggle--actief' : ''}`}
                    onClick={() => handleCategorieChange(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {/* PHEV sub-type picker */}
          {state.orientatieType === 'phev' && (
            <fieldset className="lc-fieldset">
              <legend className="lc-label">Welk type plug-in hybride overweeg je?</legend>
              <div className="lc-toggle-groep lc-toggle-groep--vertical">
                {PHEV_ORIENTATIE_TYPEN.map((type) => {
                  const isActief = state.autoModel?.model === type.model.model;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      className={`lc-toggle${isActief ? ' lc-toggle--actief' : ''}`}
                      onClick={() => handlePhevTypeSelect(type.id)}
                    >
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
        </>
      )}

      {/* Navigation */}
      <div className="lc-navigatie">
        <button
          type="button"
          className="lc-btn lc-btn--primair"
          disabled={!canProceed}
          onClick={onNext}
        >
          Volgende stap →
        </button>
      </div>
    </div>
  );
}
